import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { convertUnits, getUnitType } from '../../utils/unitConversions';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  alpha,
  Link as MuiLink,
  Chip,
  Fade,
  Card,
  CardContent,
  Grid,
  TablePagination,
  LinearProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Factory as FactoryIcon,
  Home as HomeIcon,
  LocalShipping as LocalShippingIcon,
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { LineProductionTabs } from './components';

// Date utility functions to avoid timezone issues
const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return 'N/A';
  }
};

const ProductionLinePage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { 
    canViewLineProductionPage, 
    canEditLineProductionPage, 
    canCreateLine, 
    canDeleteLine,
    canViewLineProductionOverview
  } = usePermissions();

  const [lineProductions, setLineProductions] = useState([]);
  const [filteredLineProductions, setFilteredLineProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [dateFilter, setDateFilter] = useState({
    type: 'all', // 'all', 'range', 'specific', 'expiration'
    startDate: '',
    endDate: '',
    specificDate: '',
    expirationFilter: 'all' // 'all', 'expired', 'expiring_soon', 'not_expired'
  });
  
  // Product filter dialog state
  const [productFilterDialogOpen, setProductFilterDialogOpen] = useState(false);
  const [productFilterSearchTerm, setProductFilterSearchTerm] = useState('');
  const [dialogSelectedProductFilter, setDialogSelectedProductFilter] = useState(null);
  const [productSelectionMade, setProductSelectionMade] = useState(false);
  
  // Date filter dialog state
  const [dateFilterDialogOpen, setDateFilterDialogOpen] = useState(false);
  const [tempDateFilter, setTempDateFilter] = useState({
    type: 'all',
    startDate: '',
    endDate: '',
    specificDate: '',
    expirationFilter: 'all'
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // View mode state
  const [viewMode, setViewMode] = useState('list');
  const [currentLineProduction, setCurrentLineProduction] = useState(null);
  const [products, setProducts] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [user, setUser] = useState(null);
  const [lineProductionTimeFormat, setLineProductionTimeFormat] = useState('12hour');
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedLineProduction, setSelectedLineProduction] = useState(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Create a ref for LineProductionTabs component
  const lineProductionTabsRef = useRef({ openEditDialog: () => {} });

  const [formulationIngredients, setFormulationIngredients] = useState({});
  const [lineProductionCosts, setLineProductionCosts] = useState({});

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLineProductions(),
        fetchProducts(),
        fetchRawMaterials(),
        fetchUser(),
        fetchCompanyData()
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      // Load user profile for time format preference
      if (user?.id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('batch_time_format_preference')
          .eq('id', user.id)
          .single();
          
        if (data && !error) {
          setLineProductionTimeFormat(data.batch_time_format_preference || '12hour');
        }
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };
 
  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      setCompanyName(data?.name || '');
    } catch (err) {
      console.error('Error fetching company data:', err);
    }
  };

  const fetchLineProductions = async () => {
    try {
      const { data, error } = await supabase
        .from('line_productions')
        .select(`
          *,
          product:product_id(id, name, sku)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setLineProductions(data || []);
      setFilteredLineProductions(data || []);
    } catch (err) {
      console.error('Error fetching line productions:', err);
      throw err;
    }
  };


  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from('raw_materials')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setRawMaterials(data || []);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    }
  };

  const applyFilters = () => {
    let result = [...lineProductions];
    
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(lineProduction => 
        (lineProduction.batch_number && lineProduction.batch_number.toLowerCase().includes(lowercasedSearch)) ||
        (lineProduction.lot_number && lineProduction.lot_number.toLowerCase().includes(lowercasedSearch)) ||
        (lineProduction.product?.name && lineProduction.product.name.toLowerCase().includes(lowercasedSearch)) ||
        (lineProduction.status && lineProduction.status.toLowerCase().includes(lowercasedSearch))
      );
    }
    
    if (selectedProduct) {
      result = result.filter(lineProduction => lineProduction.product_id === selectedProduct);
    }
    
    // Date filtering
    if (dateFilter.type !== 'all') {
      const today = new Date();
      
      result = result.filter(lineProduction => {
        switch (dateFilter.type) {
          case 'range':
            if (dateFilter.startDate && dateFilter.endDate) {
              const mfgDate = new Date(lineProduction.manufacture_date);
              const startDate = new Date(dateFilter.startDate);
              const endDate = new Date(dateFilter.endDate);
              return mfgDate >= startDate && mfgDate <= endDate;
            }
            return true;
          
          case 'specific':
            if (dateFilter.specificDate) {
              const mfgDate = new Date(lineProduction.manufacture_date);
              const specificDate = new Date(dateFilter.specificDate);
              return mfgDate.toDateString() === specificDate.toDateString();
            }
            return true;
          
          case 'expiration':
            if (dateFilter.expirationFilter !== 'all' && lineProduction.expiration_date) {
              const expDate = new Date(lineProduction.expiration_date);
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(today.getDate() + 30);
              
              switch (dateFilter.expirationFilter) {
                case 'expired':
                  return expDate < today;
                case 'expiring_soon':
                  return expDate >= today && expDate <= thirtyDaysFromNow;
                case 'expiring_range':
                  if (dateFilter.startDate && dateFilter.endDate) {
                    const startDate = new Date(dateFilter.startDate);
                    const endDate = new Date(dateFilter.endDate);
                    return expDate >= startDate && expDate <= endDate;
                  }
                  return true;
                case 'expiring_specific':
                  if (dateFilter.specificDate) {
                    const specificDate = new Date(dateFilter.specificDate);
                    return expDate.toDateString() === specificDate.toDateString();
                  }
                  return true;
                case 'not_expired':
                  return expDate > today;
                default:
                  return true;
              }
            }
            return dateFilter.expirationFilter === 'all';
          
          default:
            return true;
        }
      });
    }
    
    setFilteredLineProductions(result);
    setPage(0); // Reset to first page when filters change
  };

  useEffect(() => {
    fetchInitialData();
  }, [companyId]);

  const fetchLineProductionCosts = React.useCallback(async () => {
    try {
      if (lineProductions.length === 0) return;
      
      // Get all line production IDs
      const lineProductionIds = lineProductions.map(lp => lp.id);
      
      // Fetch costs from production_line_costs table
      const { data: costsData, error } = await supabase
        .from('production_line_costs')
        .select('line_production_id, total_cost, cost_per_unit, total_production_batch_cost')
        .in('line_production_id', lineProductionIds);
      
      if (error) {
        console.error('Error fetching line production costs:', error);
        return;
      }
      
      // Create a mapping of line_production_id to total_production_batch_cost
      const costsMap = {};
      if (costsData) {
        costsData.forEach(cost => {
          costsMap[cost.line_production_id] = cost.total_production_batch_cost || cost.total_cost;
        });
      }
      
      setLineProductionCosts(costsMap);
    } catch (err) {
      console.error('Error fetching line production costs:', err);
    }
  }, [lineProductions]);

  useEffect(() => {
    if (lineProductions.length > 0) {
      applyFilters();
    }
  }, [lineProductions, searchTerm, selectedProduct, dateFilter]);

  useEffect(() => {
    fetchLineProductionCosts();
  }, [fetchLineProductionCosts]);


  const fetchLineProductionDetails = async (lineProductionId) => {
    try {
      setLoading(true);
      
      const { data: lineProductionData, error: lineProductionError } = await supabase
        .from('line_productions')
        .select(`
          *,
          product:product_id(id, name, sku)
        `)
        .eq('id', lineProductionId)
        .single();
      
      if (lineProductionError) throw lineProductionError;
      
      setCurrentLineProduction(lineProductionData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching line production details:', error);
      setError('Error loading line production details: ' + error.message);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (event, lineProduction) => {
    setSelectedLineProduction(lineProduction);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteLineProduction = async () => {
    if (!selectedLineProduction) return;
    
    // Check delete permissions
    if (!canDeleteLine()) {
      setError('You do not have permission to delete line productions.');
      return;
    }
    
    setDeleting(true);
    try {
      const lineProductionId = selectedLineProduction.id;
      
      // Step 1: Get all production_line_items for this line production
      const { data: lineItems, error: itemsError } = await supabase
        .from('production_line_items')
        .select('id')
        .eq('line_production_id', lineProductionId);
      
      if (itemsError) throw itemsError;
      
      const lineItemIds = lineItems?.map(item => item.id) || [];
      
      // Step 2: Delete production_line_log_supplies that reference these line items
      if (lineItemIds.length > 0) {
        const { error: suppliesError } = await supabase
          .from('production_line_log_supplies')
          .delete()
          .in('line_item_id', lineItemIds);
        
        if (suppliesError) throw suppliesError;
      }
      
      // Step 3: Delete production_line_items (they should cascade from line_productions, but doing it explicitly to be safe)
      if (lineItemIds.length > 0) {
        const { error: deleteItemsError } = await supabase
          .from('production_line_items')
          .delete()
          .eq('line_production_id', lineProductionId);
        
        if (deleteItemsError) throw deleteItemsError;
      }
      
      // Step 4: Delete production_line_logs (they reference line_production_id)
      const { error: logsError } = await supabase
        .from('production_line_logs')
        .delete()
        .eq('line_production_id', lineProductionId);
      
      if (logsError) throw logsError;
      
      // Step 5: Delete production_line_costs (they reference line_production_id)
      const { error: costsError } = await supabase
        .from('production_line_costs')
        .delete()
        .eq('line_production_id', lineProductionId);
      
      if (costsError) throw costsError;
      
      // Step 6: Finally delete the line_productions record
      const { error } = await supabase
        .from('line_productions')
        .delete()
        .eq('id', lineProductionId);
      
      if (error) throw error;
      
      // Update both lineProductions and filteredLineProductions immediately
      const updatedLineProductions = lineProductions.filter(lp => lp.id !== selectedLineProduction.id);
      setLineProductions(updatedLineProductions);
      setFilteredLineProductions(updatedLineProductions.filter(lineProduction => {
        if (!searchTerm) return true;
        const lowercasedSearch = searchTerm.toLowerCase();
        return (
          (lineProduction.batch_number && lineProduction.batch_number.toLowerCase().includes(lowercasedSearch)) ||
          (lineProduction.lot_number && lineProduction.lot_number.toLowerCase().includes(lowercasedSearch)) ||
          (lineProduction.product?.name && lineProduction.product.name.toLowerCase().includes(lowercasedSearch)) ||
          (lineProduction.status && lineProduction.status.toLowerCase().includes(lowercasedSearch))
        );
      }));
      
      if (currentLineProduction && currentLineProduction.id === selectedLineProduction.id) {
        handleBackToList();
      }
      
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting line production:', err);
      setError(`Failed to delete line production: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditLineProduction = () => {
    if (!selectedLineProduction) return;
    handleViewDetails(selectedLineProduction);
    handleCloseMenu();
  };

  const handleViewDetails = async (lineProduction) => {
    setViewMode('detail');
    await fetchLineProductionDetails(lineProduction.id);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setCurrentLineProduction(null);
  };

  const handleCreateNew = () => {
    // Check create permissions
    if (!canCreateLine()) {
      setError('You do not have permission to create line productions.');
      return;
    }
    
    const newLineProduction = {
      batch_number: '',
      lot_number: '',
      internal_code: '',
      product_id: '',
      planned_quantity: 100,
      planned_unit_of_measure: 'pieces',
      manufacture_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      priority: 'normal',
      company_id: companyId
    };
    
    setCurrentLineProduction(newLineProduction);
    setViewMode('detail');
    
    setTimeout(() => {
      if (lineProductionTabsRef.current) {
        lineProductionTabsRef.current.openEditDialog(true);
      }
    }, 100);
  };

  const refreshAllData = async (lineProductionIdOrObject = null) => {
    try {
      await fetchLineProductions();
      
      // Handle both string ID and line production object
      let lineProductionId = null;
      if (lineProductionIdOrObject) {
        if (typeof lineProductionIdOrObject === 'string') {
          lineProductionId = lineProductionIdOrObject;
        } else if (typeof lineProductionIdOrObject === 'object' && lineProductionIdOrObject.id) {
          lineProductionId = String(lineProductionIdOrObject.id);
        }
      }
      
      if (lineProductionId) {
        await fetchLineProductionDetails(lineProductionId);
      } else if (currentLineProduction && currentLineProduction.id) {
        await fetchLineProductionDetails(currentLineProduction.id);
      }
      
      // Refresh costs after line productions are updated
      await fetchLineProductionCosts();
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    if (severity === 'error') {
      setError(message);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: 'default',
      pending_approval: 'warning',
      approved: 'info',
      scheduled: 'primary',
      in_production: 'secondary',
      quality_testing: 'warning',
      completed: 'success',
      on_hold: 'warning',
      cancelled: 'error',
      failed: 'error'
    };
    return statusColors[status] || 'default';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0 || amount === null || amount === undefined) return 'N/A';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchFormulationIngredients = async (formulationId) => {
    if (!formulationId || formulationIngredients[formulationId]) {
      return formulationIngredients[formulationId] || [];
    }

    try {
      const { data, error } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          *,
          raw_materials:ingredient_id (
            id, name, cas_number, sku, category, 
            material_type, primary_unit_measure, color, form,
            density_g_ml, primary_unit_type, primary_unit_quantity
          )
        `)
        .eq('formulation_id', formulationId)
        .order('sequence');

      if (error) throw error;
      
      const ingredients = data || [];
      setFormulationIngredients(prev => ({
        ...prev,
        [formulationId]: ingredients
      }));
      
      return ingredients;
    } catch (error) {
      console.error('Error loading formulation ingredients:', error);
      return [];
    }
  };

  // Product filter dialog functions
  const getFilteredProductsForFilter = () => {
    if (!productFilterSearchTerm) return products;
    return products.filter(product =>
      product.name.toLowerCase().includes(productFilterSearchTerm.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(productFilterSearchTerm.toLowerCase()))
    );
  };

  const handleOpenProductFilterDialog = () => {
    setProductFilterDialogOpen(true);
    setProductFilterSearchTerm('');
    setDialogSelectedProductFilter(null);
    setProductSelectionMade(false);
  };

  const handleCloseProductFilterDialog = () => {
    setProductFilterDialogOpen(false);
    setProductFilterSearchTerm('');
    setDialogSelectedProductFilter(null);
    setProductSelectionMade(false);
  };

  const handleDialogProductFilterSelect = (product) => {
    setDialogSelectedProductFilter(product);
    setProductSelectionMade(true);
  };

  const handleConfirmProductFilterSelection = () => {
    if (dialogSelectedProductFilter === 'all') {
      setSelectedProduct('');
    } else if (dialogSelectedProductFilter) {
      setSelectedProduct(dialogSelectedProductFilter.id);
    } else {
      setSelectedProduct('');
    }
    setProductFilterDialogOpen(false);
    setProductFilterSearchTerm('');
    setDialogSelectedProductFilter(null);
    setProductSelectionMade(false);
  };

  const getSelectedProductName = () => {
    if (!selectedProduct) return 'All Products';
    const product = products.find(p => p.id === selectedProduct);
    return product ? product.name : 'Unknown Product';
  };

  // Date filter dialog functions
  const handleOpenDateFilterDialog = () => {
    setTempDateFilter({ ...dateFilter });
    setDateFilterDialogOpen(true);
  };

  const handleCloseDateFilterDialog = () => {
    setDateFilterDialogOpen(false);
    setTempDateFilter({
      type: 'all',
      startDate: '',
      endDate: '',
      specificDate: '',
      expirationFilter: 'all'
    });
  };

  const handleConfirmDateFilterSelection = () => {
    setDateFilter({ ...tempDateFilter });
    setDateFilterDialogOpen(false);
  };

  const getDateFilterLabel = () => {
    switch (dateFilter.type) {
      case 'range':
        if (dateFilter.startDate && dateFilter.endDate) {
          return `${dayjs(dateFilter.startDate + 'T12:00:00').format('M/D/YYYY')} - ${dayjs(dateFilter.endDate + 'T12:00:00').format('M/D/YYYY')}`;
        }
        return 'Date Range';
      case 'specific':
        if (dateFilter.specificDate) {
          return dayjs(dateFilter.specificDate + 'T12:00:00').format('M/D/YYYY');
        }
        return 'Specific Date';
      case 'expiration':
        const expLabels = {
          'all': 'All Items',
          'expired': 'Expired Items',
          'expiring_soon': 'Expiring Soon',
          'expiring_range': dateFilter.startDate && dateFilter.endDate ? 
            `${dayjs(dateFilter.startDate + 'T12:00:00').format('M/D/YYYY')} - ${dayjs(dateFilter.endDate + 'T12:00:00').format('M/D/YYYY')}` : 
            'Expiring in Range',
          'expiring_specific': dateFilter.specificDate ? 
            `${dayjs(dateFilter.specificDate + 'T12:00:00').format('M/D/YYYY')}` : 
            'Expiring on Day',
          'not_expired': 'Not Expired'
        };
        return `Expiration: ${expLabels[dateFilter.expirationFilter] || 'All Items'}`;
      default:
        return 'All Dates';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProduct('');
    setDateFilter({
      type: 'all',
      startDate: '',
      endDate: '',
      specificDate: '',
      expirationFilter: 'all'
    });
  };

  // Permission check for view access
  if (!canViewLineProductionPage()) {
    // Special case: User has create permission but lacks view/edit permissions
    if (canCreateLine()) {
      return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '70vh',
            textAlign: 'center'
          }}>
            <Card sx={{
              p: 8,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              background: 'white',
              boxShadow: `0 12px 40px ${alpha(theme.palette.warning.main, 0.08)}`,
              maxWidth: 700,
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: `0 20px 60px ${alpha(theme.palette.warning.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                opacity: 0.8,
                transition: 'opacity 0.3s ease'
              },
              '&:hover::before': {
                opacity: 1
              }
            }}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.warning.main, 0.05)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                mb: 6,
                display: 'inline-flex',
                boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.15)}`
              }}>
                <FactoryIcon sx={{ fontSize: 48, color: 'warning.main' }} />
              </Box>
              <Typography variant="h3" sx={{ 
                mb: 3, 
                fontWeight: 500, 
                color: 'text.primary',
                fontSize: '2.2rem',
                letterSpacing: '-0.8px'
              }}>
                Limited Access
              </Typography>
              <Typography variant="h6" sx={{ 
                color: 'warning.main', 
                mb: 4,
                fontWeight: 500,
                fontSize: '1.2rem'
              }}>
                Additional Permissions Required
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary',
                lineHeight: 1.7,
                mb: 4,
                fontSize: '1.05rem',
                maxWidth: 550,
                mx: 'auto'
              }}>
                You have permission to <strong>create line productions</strong>, but you need additional 
                <strong> view and edit permissions</strong> to access the Production Line Overview and manage existing productions.
                {!canViewLineProductionOverview() && (
                  <Box component="span" sx={{ display: 'block', mt: 2, fontStyle: 'italic', color: 'warning.main' }}>
                    Note: You also lack access to the Production Line Overview component (ProductionLineOverview.jsx).
                  </Box>
                )}
              </Typography>
              <Box sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                mb: 6,
                maxWidth: 500,
                mx: 'auto'
              }}>
                <Typography variant="body2" sx={{ 
                  color: 'info.main',
                  fontWeight: 500,
                  mb: 2,
                  fontSize: '0.95rem'
                }}>
                  Required Permissions for Full Access:
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  lineHeight: 1.6
                }}>
                  • <strong>View Line Production Page</strong> - To see the production list<br/>
                  • <strong>Edit Line Production Page</strong> - To modify existing productions<br/>
                  • <strong>View Line Production Overview</strong> - To access detailed production data in ProductionLineOverview.jsx
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Return to Previous Page
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateNew}
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    bgcolor: 'warning.main',
                    '&:hover': {
                      bgcolor: 'warning.dark',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.warning.main, 0.4)}`
                    }
                  }}
                >
                  Create New Production
                </Button>
              </Stack>
            </Card>
          </Box>
        </Container>
      );
    }

    // Standard access denied for users without any permissions
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <FactoryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Line Production Management Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Line Production Management. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // List view render
  const renderLineProductionsList = () => (
    <Fade in timeout={800}>
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          {/* Enhanced Page Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 5,
            mt: 5,
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '200px',
              height: '200px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
              zIndex: 0
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }} />
                <Typography variant="caption" sx={{ 
                  color: 'info.main', 
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.75rem'
                }}>
                  Line Production Management
                </Typography>
              </Box>
              
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                fontSize: '1.8rem',
                mb: 1,
                lineHeight: 1.2
              }}>
                Line Productions
              </Typography>
              
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '0.9rem',
                mb: 2.5,
                maxWidth: '400px'
              }}>
                Comprehensive line production tracking from planning to completion with detailed status monitoring
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${filteredLineProductions.length} ${filteredLineProductions.length === 1 ? 'Production' : 'Productions'}`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }} 
                />
                <Chip 
                  label={`${filteredLineProductions.filter(lp => lp.status === 'completed').length} Completed`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                  }} 
                />
                <Chip 
                  label={`${filteredLineProductions.filter(lp => ['in_production', 'quality_testing'].includes(lp.status)).length} Active`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }} 
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
              {canCreateLine() && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    bgcolor: 'primary.main',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                    }
                  }}
                >
                  New Line Production
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Search and Filter Bar */}
        <Box sx={{ mb: 8 }}>
          <Paper 
            sx={{ 
              p: 4, 
              mb: 5, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 500,
                color: 'primary.main',
                fontSize: '1.25rem'
              }}
            >
              Search & Filter Line Productions
            </Typography>
            
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Search
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search line productions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: 56,
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '1rem',
                        fontWeight: 500,
                        py: 2,
                        px: 2
                      },
                      '& .MuiOutlinedInput-root': {
                        height: 56,
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                    size="medium"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Product
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpenProductFilterDialog}
                    sx={{
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      height: 56,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      color: 'text.primary',
                      fontWeight: 500,
                      px: 2,
                      textTransform: 'none',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}
                    endIcon={<ExpandMoreIcon />}
                  >
                    {getSelectedProductName()}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Date Filter
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpenDateFilterDialog}
                    sx={{
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      height: 56,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      color: 'text.primary',
                      fontWeight: 500,
                      px: 2,
                      textTransform: 'none',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}
                    endIcon={<ExpandMoreIcon />}
                  >
                    {getDateFilterLabel()}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1, height: 56, alignItems: 'center' }}>
                  <Tooltip title="Clear All Filters">
                    <IconButton 
                      onClick={clearFilters}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: 'white',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.04),
                          borderColor: theme.palette.error.main,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
                        }
                      }}
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={fetchLineProductions}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: 'white',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Line Productions Content */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
        >
          <Box sx={{ bgcolor: 'transparent', p: 4 }}>
            {/* Line Productions Table */}
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={48} />
                </Box>
              ) : (
                <TableContainer sx={{ overflow: 'visible' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Batch #</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Lot #</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Product</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Quantity</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Status</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Mfg Date</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Expiration</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}>Total Cost</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3,
                          width: 120
                        }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLineProductions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                            <Stack spacing={2} alignItems="center">
                              <FactoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {searchTerm ? 'No line productions found matching your search' : 'No line productions yet'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {searchTerm ? 'Try adjusting your search terms' : 'Create your first line production to get started'}
                              </Typography>
                              {!searchTerm && canCreateLine() && (
                                <Button
                                  variant="contained"
                                  startIcon={<AddIcon />}
                                  onClick={handleCreateNew}
                                  sx={{ 
                                    mt: 2,
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    textTransform: 'none',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': {
                                      transform: 'translateY(-1px) scale(1.02)',
                                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                                    }
                                  }}
                                >
                                  Create First Line Production
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (filteredLineProductions.length > 0 
                          ? filteredLineProductions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : []
                        ).map((lineProduction) => (
                          <TableRow 
                            key={lineProduction.id} 
                            hover 
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': { 
                                bgcolor: 'grey.50',
                                transform: 'translateX(4px)',
                                boxShadow: 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 2.5,
                                px: 3
                              }
                            }}
                            onClick={() => handleViewDetails(lineProduction)}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                {lineProduction.batch_number || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                {lineProduction.lot_number || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                {lineProduction.product?.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                {(() => {
                                  // Calculate from stored unit configuration values
                                  if (lineProduction.unit_configuration_id && 
                                      (lineProduction.pallet_quantity > 0 || lineProduction.case_quantity > 0 || lineProduction.unit_quantity > 0)) {
                                    
                                    // Use the stored breakdown values to calculate total
                                    let totalUnits = 0;
                                    const pallets = lineProduction.pallet_quantity || 0;
                                    const cases = lineProduction.case_quantity || 0;
                                    const units = lineProduction.unit_quantity || 0;
                                    
                                    // Calculate total based on actual unit configuration from database
                                    // Need to fetch the unit configuration to get the real values
                                    return `${(lineProduction.planned_quantity || 0).toLocaleString()} units`;
                                                                      }
                                  
                                  // Fallback to variant distribution or planned quantity
                                  if (lineProduction.variant_distribution && lineProduction.variant_distribution.length > 0) {
                                    const totalUnits = lineProduction.variant_distribution.reduce((sum, v) => sum + v.quantity, 0);
                                    return `${totalUnits.toLocaleString()} units`;
                                  }
                                  return `${(lineProduction.planned_quantity || 0).toLocaleString()} ${lineProduction.planned_unit_of_measure || 'units'}`;
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={formatStatus(lineProduction.status)}
                                color={getStatusColor(lineProduction.status)}
                                size="small"
                                sx={{ 
                                  minWidth: 100,
                                  fontWeight: 500,
                                  borderRadius: 2,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                {formatDateForDisplay(lineProduction.manufacture_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                {formatDateForDisplay(lineProduction.expiration_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500, 
                                fontSize: '0.95rem', 
                                color: lineProductionCosts[lineProduction.id] ? '#2e7d32' : 'text.secondary' 
                              }}>
                                {lineProductionCosts[lineProduction.id] ? 
                                  formatCurrency(lineProductionCosts[lineProduction.id]) : 
                                  'Not calculated'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                disabled={!canEditLineProductionPage() && !canDeleteLine()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canEditLineProductionPage() || canDeleteLine()) {
                                    handleOpenMenu(e, lineProduction);
                                  }
                                }}
                                sx={{ 
                                  p: 1,
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  bgcolor: (!canEditLineProductionPage() && !canDeleteLine()) ? 
                                    alpha(theme.palette.grey[400], 0.3) : 'black',
                                  color: (!canEditLineProductionPage() && !canDeleteLine()) ? 
                                    alpha(theme.palette.grey[500], 0.6) : 'white',
                                  cursor: (!canEditLineProductionPage() && !canDeleteLine()) ? 
                                    'not-allowed' : 'pointer',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': (!canEditLineProductionPage() && !canDeleteLine()) ? {
                                    bgcolor: alpha(theme.palette.grey[400], 0.3),
                                    color: alpha(theme.palette.grey[500], 0.6),
                                    transform: 'none',
                                    boxShadow: 'none'
                                  } : { 
                                    bgcolor: 'grey.900',
                                    color: 'white',
                                    transform: 'translateY(-2px) scale(1.05)',
                                    boxShadow: `0 6px 18px ${alpha(theme.palette.grey[900], 0.4)}`,
                                    border: `1px solid ${theme.palette.grey[900]}`
                                  },
                                  '&.Mui-disabled': {
                                    bgcolor: alpha(theme.palette.grey[400], 0.3),
                                    color: alpha(theme.palette.grey[500], 0.6),
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
                                  }
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!loading && (
                <Box sx={{ 
                  borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                  borderRadius: '0 0 12px 12px'
                }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredLineProductions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ 
                      px: 3,
                      py: 2,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      },
                      '& .MuiTablePagination-select': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          margin: '0 2px',
                          background: 'white',
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: theme.palette.primary.main,
                            color: 'white',
                            transform: 'translateY(-1px)',
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                          },
                          '&.Mui-disabled': {
                            background: alpha(theme.palette.grey[300], 0.5),
                            color: theme.palette.grey[500],
                            border: `1px solid ${theme.palette.grey[300]}`,
                            transform: 'none',
                            boxShadow: 'none'
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Box>
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
            }
          }}
        >
          {canEditLineProductionPage() && (
            <MenuItem 
              onClick={handleEditLineProduction}
              sx={{
                py: 1.5,
                px: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Line Production</ListItemText>
            </MenuItem>
          )}
          {canDeleteLine() && (
            <MenuItem 
              onClick={handleOpenDeleteDialog} 
              sx={{ 
                color: 'error.main',
                py: 1.5,
                px: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.08)
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Line Production</ListItemText>
            </MenuItem>
          )}
          {!canEditLineProductionPage() && !canDeleteLine() && (
            <MenuItem 
              disabled
              sx={{
                py: 1.5,
                px: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                color: 'text.disabled'
              }}
            >
              <ListItemText>No actions available</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary'
          }}>
            Delete Line Production
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
              Are you sure you want to delete the line production "{selectedLineProduction?.batch_number}"? 
              This action cannot be undone and will remove all associated data including ingredients, 
              equipment usage, costs, and production logs.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCloseDeleteDialog}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteLineProduction} 
              color="error" 
              variant="contained"
              disabled={deleting}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}`
                }
              }}
            >
              {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Filter Dialog */}
        <Dialog
          open={productFilterDialogOpen}
          onClose={handleCloseProductFilterDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 4,
              height: '40vh',
              width: '600px',
              maxWidth: '600px',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Product
            </Typography>
            <IconButton onClick={handleCloseProductFilterDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={productFilterSearchTerm}
                onChange={(e) => setProductFilterSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow 
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          All Products
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Show all line productions
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogProductFilterSelect('all')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedProductFilter === 'all' ? 'black' : 'transparent',
                          color: dialogSelectedProductFilter === 'all' ? 'white' : 'inherit',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            bgcolor: dialogSelectedProductFilter === 'all' ? 'black' : 'transparent',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                  {getFilteredProductsForFilter().map((product) => (
                    <TableRow 
                      key={product.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {product.name}
                          </Typography>
                          {product.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {product.sku}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDialogProductFilterSelect(product)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedProductFilter?.id === product.id ? 'black' : 'transparent',
                            color: dialogSelectedProductFilter?.id === product.id ? 'white' : 'inherit',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              bgcolor: dialogSelectedProductFilter?.id === product.id ? 'black' : 'transparent',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredProductsForFilter().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {productFilterSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={() => window.open(`/company/${companyId}/products`, '_blank')}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
                color: 'success.main',
                '&:hover': {
                  border: `1px solid ${theme.palette.success.main}`,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Product
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseProductFilterDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmProductFilterSelection}
                variant="contained"
                disabled={!productSelectionMade}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Date Filter Dialog */}
        <Dialog
          open={dateFilterDialogOpen}
          onClose={handleCloseDateFilterDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 4,
              width: '600px',
              maxWidth: '600px',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Date Filter Options
            </Typography>
            <IconButton onClick={handleCloseDateFilterDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel sx={{ fontWeight: 500 }}>Filter Type</InputLabel>
                  <Select
                    value={tempDateFilter.type}
                    label="Filter Type"
                    onChange={(e) => setTempDateFilter(prev => ({ ...prev, type: e.target.value }))}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.grey[400], 0.8)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <MenuItem value="all">All Dates</MenuItem>
                    <MenuItem value="range">Date Range</MenuItem>
                    <MenuItem value="specific">Specific Date</MenuItem>
                    <MenuItem value="expiration">Expiration Filter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {tempDateFilter.type === 'range' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={tempDateFilter.startDate ? dayjs(tempDateFilter.startDate + 'T12:00:00') : null}
                        onChange={(newValue) => {
                          setTempDateFilter(prev => ({ 
                            ...prev, 
                            startDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          }));
                        }}
                        format="MM/DD/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: alpha(theme.palette.grey[400], 0.8)
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        value={tempDateFilter.endDate ? dayjs(tempDateFilter.endDate + 'T12:00:00') : null}
                        onChange={(newValue) => {
                          setTempDateFilter(prev => ({ 
                            ...prev, 
                            endDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          }));
                        }}
                        format="MM/DD/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: alpha(theme.palette.grey[400], 0.8)
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}

              {tempDateFilter.type === 'specific' && (
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Specific Date"
                      value={tempDateFilter.specificDate ? dayjs(tempDateFilter.specificDate + 'T12:00:00') : null}
                        onChange={(newValue) => {
                          setTempDateFilter(prev => ({ 
                            ...prev, 
                            specificDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          }));
                        }}
                        format="MM/DD/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: alpha(theme.palette.grey[400], 0.8)
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              {tempDateFilter.type === 'expiration' && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ fontWeight: 500 }}>Expiration Status</InputLabel>
                      <Select
                        value={tempDateFilter.expirationFilter}
                        label="Expiration Status"
                        onChange={(e) => setTempDateFilter(prev => ({ ...prev, expirationFilter: e.target.value }))}
                        sx={{
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.grey[400], 0.8)
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <MenuItem value="all">All Items</MenuItem>
                        <MenuItem value="expired">Expired Items</MenuItem>
                        <MenuItem value="expiring_soon">Expiring Soon (30 days)</MenuItem>
                        <MenuItem value="expiring_range">Expiring in Date Range</MenuItem>
                        <MenuItem value="expiring_specific">Expiring on Specific Day</MenuItem>
                        <MenuItem value="not_expired">Not Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {tempDateFilter.expirationFilter === 'expiring_specific' && (
                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Expiration Date"
                          value={tempDateFilter.specificDate ? dayjs(tempDateFilter.specificDate + 'T12:00:00') : null}
                          onChange={(newValue) => {
                            setTempDateFilter(prev => ({ 
                              ...prev, 
                              specificDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                            }));
                          }}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': {
                                    borderColor: alpha(theme.palette.grey[400], 0.8)
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                  
                  {(tempDateFilter.expirationFilter === 'expiring_soon' || tempDateFilter.expirationFilter === 'expiring_range') && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Expiration Start Date"
                            value={tempDateFilter.startDate ? dayjs(tempDateFilter.startDate + 'T12:00:00') : null}
                            onChange={(newValue) => {
                              setTempDateFilter(prev => ({ 
                                ...prev, 
                                startDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                              }));
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                sx: {
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.grey[400], 0.8)
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Expiration End Date"
                            value={tempDateFilter.endDate ? dayjs(tempDateFilter.endDate + 'T12:00:00') : null}
                            onChange={(newValue) => {
                              setTempDateFilter(prev => ({ 
                                ...prev, 
                                endDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                              }));
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                sx: {
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.grey[400], 0.8)
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Button
              onClick={handleCloseDateFilterDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDateFilterSelection}
              variant="contained"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );

  // Detail view render
  const renderLineProductionDetail = () => {
    if (!currentLineProduction) return null;

    return (
      <Container disableGutters maxWidth={false} sx={{ 
        overflow: 'visible', 
        px: 2,
        pl: 3,
        pr: 3,
        maxWidth: '100%',
        width: '100%'
      }}>
        <LineProductionTabs
          ref={lineProductionTabsRef}
          lineProduction={currentLineProduction}
          products={products}
          rawMaterials={rawMaterials}
          showSnackbar={showSnackbar}
          user={user}
          userCompanyId={companyId}
          onUpdate={refreshAllData}
          onBackToList={handleBackToList}
          lineProductionTimeFormat={lineProductionTimeFormat}
        />
      </Container>
    );
  };

  if (loading && viewMode === 'list') {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <FactoryIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Line Productions
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing line production and manufacturing data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Box>
      {viewMode === 'list' ? (
        <Fade in={viewMode === 'list'} key="list-view" timeout={600}>
          <Box>
            {renderLineProductionsList()}
          </Box>
        </Fade>
      ) : (
        <Fade in={viewMode === 'detail'} key="detail-view" timeout={600}>
          <Box>
            {renderLineProductionDetail()}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default ProductionLinePage;